// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * React context provider for collection storage mode configuration.
 *
 * Wraps QueryClientProvider and exposes a D1Client instance plus
 * a mode resolver. When the provider is not mounted, useCollectionMode()
 * returns null — all hooks fall back to the existing local-only RxDB path.
 */

import React, { createContext, useContext, useMemo } from 'react'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import type { CollectionModeConfig, CollectionMode } from '../../config/CollectionModeConfig'
import { getCollectionMode } from '../../config/CollectionModeConfig'
import { D1Client } from '../remote/d1Client'

export interface CollectionModeContextValue {
  config: CollectionModeConfig
  queryClient: QueryClient
  client: D1Client
  getMode: (collectionName: string) => CollectionMode
}

const CollectionModeContext = createContext<CollectionModeContextValue | null>(null)

export interface CollectionModeProviderProps {
  config: CollectionModeConfig
  queryClient: QueryClient
  children: React.ReactNode
}

export function CollectionModeProvider({
  config,
  queryClient,
  children,
}: CollectionModeProviderProps) {
  const client = useMemo(
    () => new D1Client(config),
    [config.apiBaseUrl, config.getAuthToken],
  )

  const value = useMemo<CollectionModeContextValue>(
    () => ({
      config,
      queryClient,
      client,
      getMode: (name: string) => getCollectionMode(config, name),
    }),
    [config, queryClient, client],
  )

  return (
    <QueryClientProvider client={queryClient}>
      <CollectionModeContext.Provider value={value}>
        {children}
      </CollectionModeContext.Provider>
    </QueryClientProvider>
  )
}

/**
 * Access the collection mode context.
 *
 * Returns `null` when the provider is not mounted — consumers should
 * treat null as "all collections are local" for backward compatibility.
 */
export function useCollectionMode(): CollectionModeContextValue | null {
  return useContext(CollectionModeContext)
}
